"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { track } from "@/lib/analytics";
import { recordNav } from "@/lib/pageContext";

/**
 * Marketing-funnel instrumentation for the static pages (homepage, trades, etc.).
 *
 * This is the Next.js port of the section/scroll/CTA tracking that lived in the
 * legacy ramskit.co.uk static build (dist/shared.js). It was dropped when the
 * site moved to this app, so the homepage emitted only autocaptured $pageview —
 * the entire landing→builder funnel was analytically dark. See [[project_ramsdocs_analytics_gaps]].
 *
 * Three signals, all best-effort and blocker-safe (track() no-ops if PostHog isn't ready):
 *   1. Section views — IntersectionObserver over [data-view-event]; fires the
 *      named event once when the section first enters the viewport.
 *   2. Scroll depth — scroll_50 / scroll_75, once per session (sessionStorage).
 *   3. CTA clicks — click delegation over [data-track-click]; fires the named event.
 *
 * Declarative, mirroring the legacy build: pages add the attributes, this owns the wiring.
 */
const SCROLL_KEY = "ph_scroll_fired";
const SCROLL_THRESHOLDS: { pct: number; name: string }[] = [
  { pct: 0.5, name: "scroll_50" },
  { pct: 0.75, name: "scroll_75" },
];

export function FunnelTracking() {
  const pathname = usePathname();

  // Record the visited page for the help assistant's journey context. The title
  // updates a tick after navigation, so capture on the next frame.
  useEffect(() => {
    const id = requestAnimationFrame(() => recordNav(pathname, document.title));
    return () => cancelAnimationFrame(id);
  }, [pathname]);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;

    // 1. Section-view events
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const name = (entry.target as HTMLElement).dataset.viewEvent;
          if (name) track(name);
          io.unobserve(entry.target);
        }
      },
      // Fire once a section enters the lower viewport. A small threshold (not 0.4)
      // so sections taller than the viewport — e.g. the hero — still qualify.
      { rootMargin: "0px 0px -15% 0px", threshold: 0.01 }
    );
    document.querySelectorAll("[data-view-event]").forEach((el) => io.observe(el));

    // 2. Scroll-depth events (once per session, per threshold)
    function firedSet(): Record<string, boolean> {
      try {
        return JSON.parse(sessionStorage.getItem(SCROLL_KEY) || "{}");
      } catch {
        return {};
      }
    }
    function checkScroll() {
      const scrolled = window.scrollY + window.innerHeight;
      const docH = document.documentElement.scrollHeight;
      const fired = firedSet();
      let remaining = 0;
      for (const t of SCROLL_THRESHOLDS) {
        if (fired[t.name]) continue;
        if (scrolled >= docH * t.pct) {
          fired[t.name] = true;
          track(t.name);
        } else {
          remaining++;
        }
      }
      try {
        sessionStorage.setItem(SCROLL_KEY, JSON.stringify(fired));
      } catch {
        /* ignore */
      }
      if (remaining === 0) window.removeEventListener("scroll", checkScroll);
    }
    window.addEventListener("scroll", checkScroll, { passive: true });
    checkScroll();

    // 3. CTA click delegation
    function onClick(e: MouseEvent) {
      const el = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-track-click]");
      const name = el?.dataset.trackClick;
      if (name) track(name);
    }
    document.addEventListener("click", onClick);

    return () => {
      io.disconnect();
      window.removeEventListener("scroll", checkScroll);
      document.removeEventListener("click", onClick);
    };
  }, [pathname]);

  return null;
}
